import React, { createContext, useContext, useEffect } from "react";
import { useSyncWorker, SyncState } from "../hooks/useSyncWorker";
import { useNetworkStatus, NetworkStatus } from "../hooks/useNetworkStatus";

interface SyncContextValue {
  syncState: SyncState;
  networkStatus: NetworkStatus;
  startAutoSync: () => void;
  stopAutoSync: () => void;
  syncNow: () => Promise<void>;
  updateSyncInterval: (interval: number) => void;
}

const SyncContext = createContext<SyncContextValue | undefined>(undefined);

export const SyncProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const syncWorker = useSyncWorker();
  const networkStatus = useNetworkStatus();

  // Pausar sincronização quando offline
  useEffect(() => {
    if (!networkStatus.isOnline && syncWorker.syncState.isActive) {
      console.log("📵 Pausando sincronização (offline)");
      syncWorker.stopAutoSync();
    } else if (networkStatus.wasOffline && networkStatus.isOnline) {
      console.log("🌐 Retomando sincronização (online)");
      syncWorker.startAutoSync();
    }
  }, [networkStatus.isOnline, networkStatus.wasOffline, syncWorker.syncState.isActive]);

  const value: SyncContextValue = {
    syncState: syncWorker.syncState,
    networkStatus,
    startAutoSync: syncWorker.startAutoSync,
    stopAutoSync: syncWorker.stopAutoSync,
    syncNow: syncWorker.syncNow,
    updateSyncInterval: syncWorker.updateSyncInterval,
  };

  return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>;
};

export const useSync = (): SyncContextValue => {
  const context = useContext(SyncContext);
  if (!context) {
    throw new Error("useSync deve ser usado dentro de um SyncProvider");
  }
  return context;
};

export default SyncContext;
